import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FormHelper,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxIconButtonComponent,
  onyxMaxNumberValidator,
  onyxMinNumberValidator,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { isEqual } from 'lodash';
import { distinctUntilChanged, map, startWith } from 'rxjs';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../../common/services/preferences.service';
import { PaymentTermType } from '../../common/enums/payment-term-type';

type ContractorPaymentFormGroup = ReturnType<
  (typeof ContractorPaymentFormComponent)['buildForm']
>;

type ContractorPaymentCashDiscountFormGroup = ReturnType<
  (typeof ContractorPaymentFormComponent)['buildCashDiscountForm']
>;

@Component({
  selector: 'app-contractor-payment-form',
  imports: [
    OnyxSectionComponent,
    OnyxDropdownComponent,
    OnyxTextFieldComponent,
    OnyxSuggestionsComponent,
    ReactiveFormsModule,
    AsyncPipe,
    OnyxFormGroupComponent,
    TranslatePipe,
    OnyxToggleComponent,
    OnyxButtonComponent,
    OnyxIconButtonComponent,
  ],
  templateUrl: './contractor-payment-form.component.html',
  styleUrl: './contractor-payment-form.component.scss',
})
export class ContractorPaymentFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly form = input.required<ContractorPaymentFormGroup>();

  protected readonly I18N = 'contractors.contractorForm.payment';
  protected readonly TERM_DAYS_SUGGESTIONS = [14, 30].map((value) => ({
    name: value.toString(),
    value,
  }));
  protected readonly TERM_TYPES_SUGGESTIONS = [
    PaymentTermType.LAST_UNLOADING,
  ].map((value) => ({
    name: `${DictionaryCode.PAYMENT_TERM_TYPE}.${value}`,
    value,
  }));

  protected readonly termTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.PAYMENT_TERM_TYPE,
  );
  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );

  protected hasTradeCreditLimit = signal(false);
  protected hasCashDiscount = signal(false);

  private cashDiscountControl = computed(
    () => this.form().controls.parameters.controls.cashDiscount,
  );

  public ngOnInit(): void {
    const form = this.form();
    const controls = form.controls;

    effect(
      () => {
        ValidationHelper.toggleControls(
          controls.parameters.controls.tradeCreditLimit,
          { enabled: this.hasTradeCreditLimit(), reset: true },
        );

        ValidationHelper.toggleControls(this.cashDiscountControl(), {
          enabled: this.hasCashDiscount(),
          reset: true,
        });
        if (this.hasCashDiscount() && !this.cashDiscountControl().length) {
          this.addDiscount();
        }
      },
      { injector: this.injector },
    );

    form.valueChanges
      .pipe(
        startWith(undefined),
        map(() => form.getRawValue()),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        ValidationHelper.toggleControls(controls.selectedTermDay, {
          enabled: value.termType === PaymentTermType.SELECTED_DAY,
          reset: true,
        });

        this.hasTradeCreditLimit.update(
          (force) => force || value.parameters.tradeCreditLimit.value != null,
        );

        const discountsLength = value.parameters.cashDiscount.length;
        this.hasCashDiscount.set(!!discountsLength);
      });
  }

  protected addDiscount(): void {
    this.cashDiscountControl().push(
      ContractorPaymentFormComponent.buildCashDiscountForm(this.fb),
    );
  }

  protected removeDiscount(index: number): void {
    this.cashDiscountControl().removeAt(index);
    if (!this.cashDiscountControl().length) this.addDiscount();
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      termDays: fb.control<number | null>(null, [Validators.required]),
      termType: fb.control<PaymentTermType | null>(null, [Validators.required]),
      selectedTermDay: fb.control<number | null>(
        { value: null, disabled: true },
        [
          Validators.required,
          onyxMinNumberValidator(1),
          onyxMaxNumberValidator(28),
        ],
      ),
      parameters: fb.group({
        tradeCreditLimit: FormHelper.buildAmountForm(fb, preferencesService, {
          minValue: Number.EPSILON,
        }),
        cashDiscount: fb.array<ContractorPaymentCashDiscountFormGroup>([]),
        hasFactoringConsent: fb.control(false),
      }),
    });
  }

  public static buildCashDiscountForm(fb: NonNullableFormBuilder) {
    return fb.group({
      percentage: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(0),
        onyxMaxNumberValidator(100 * 10 ** 2),
      ]),
      termDays: fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
      ]),
    });
  }
}
